import { useAuth } from "../../context/Auth";
import { DeleteIcon } from "../../icons/Icon";
import prodStyles from "./ProdLineInfoCard.module.css";

/**
 * ProdLineInfoCard
 * ─────────────────────────────────────────────────────────────
 * Props:
 *   lineName         string   — machine / line name
 *   lineStatus       string   — "running" | "idle" | "maintenance"
 *   manufacturer     string   — shown below name
 *   subText          string   — products or any second sub-line
 *   capacity         number   — pcs/day  (hidden when undefined)
 *   workingHour      number   — hrs/day  (hidden when undefined)
 *   imageUri         string   — optional image (falls back to initial)
 *   showStatus       bool     — show/hide status pill + dot  (default true)
 *   showManufacturer bool     — show/hide manufacturer line  (default true)
 *   showSubText      bool     — show/hide subText line       (default true)
 *   showFooter       bool     — show/hide capacity+hours row (default true)
 *   onClick          fn       — card click handler
 */
const ProdLineInfoCard = ({
  lineName = "",
  lineStatus = "",
  manufacturer = "",
  subText = "",
  capacity,
  workingHour,
  imageUri,
  showStatus = true,
  showManufacturer = true,
  showSubText = true,
  showFooter = true,
  onClick,
  warehouse,
  warehouseId,
}) => {
  const { mode } = useAuth();

  const STATUS_MAP = {
    running: {
      label: "Running",
      dot: "#1a8c52",
      bg: "#e6f9f0",
      color: "#1a8c52",
    },
    idle: { label: "Idle", dot: "#f0a500", bg: "#fff8e6", color: "#b07d10" },
    maintenance: {
      label: "Maintenance",
      dot: "#e74c3c",
      bg: "#fdeaea",
      color: "#c0392b",
    },
    stopped: {
      label: "Stopped",
      dot: "#db0707",
      bg: "#fdd7d7",
      color: "#db0707",
    },
  };

  const statusConfig = STATUS_MAP[lineStatus?.toLowerCase()] || null;

  return (
    <div
      style={{ position: "relative" }}
      className={`${prodStyles.card} ${mode ? prodStyles.card_dark : ""}`}
      onClick={onClick}
    >
      {/* ── Top: avatar + text ────────────────────────────── */}
      <div className={prodStyles.top}>
        {/* Avatar */}
        <div className={prodStyles.avatar}>
          {imageUri ? (
            <img
              src={`${process.env.REACT_APP_S3URL}${imageUri}`}
              alt={lineName}
              className={prodStyles.avatarImg}
            />
          ) : (
            <span
              className={`${prodStyles.avatarInitial} ${mode ? prodStyles.avatarInitial_dark : ""}`}
            >
              {lineName.charAt(0).toUpperCase()}
            </span>
          )}

          {/* Status dot on avatar corner */}
          {showStatus && statusConfig && (
            <span
              className={prodStyles.avatarDot}
              style={{ backgroundColor: statusConfig.dot }}
            />
          )}
        </div>

        {/* Text */}
        <div className={prodStyles.textBlock}>
          <div className={prodStyles.nameRow}>
            <p
              className={`${prodStyles.lineName} ${mode ? prodStyles.lineName_dark : ""}`}
            >
              {lineName}
            </p>
          </div>
          <div>
            {showStatus && statusConfig && (
              <span
                className={prodStyles.statusPill}
                style={{
                  backgroundColor: statusConfig.bg,
                  color: statusConfig.color,
                }}
              >
                <span
                  className={prodStyles.pillDot}
                  style={{ backgroundColor: statusConfig.dot }}
                />
                {statusConfig.label}
              </span>
            )}
          </div>
          {showManufacturer && manufacturer && (
            <p
              className={`${prodStyles.manufacturer} ${mode ? prodStyles.manufacturer_dark : ""}`}
            >
              {manufacturer}
            </p>
          )}

          {showSubText && subText && (
            <p
              className={`${prodStyles.subText} ${mode ? prodStyles.subText_dark : ""}`}
            >
              {subText}
            </p>
          )}

          {warehouse && (
            <p
              className={`${prodStyles.subText} ${mode ? prodStyles.subText_dark : ""}`}
            >
              {warehouse}
            </p>
          )}
        </div>
      </div>

      {/* ── Footer: capacity + hours chips ───────────────── */}
      {showFooter && (capacity !== undefined || workingHour !== undefined) && (
        <div
          className={`${prodStyles.footer} ${mode ? prodStyles.footer_dark : ""}`}
        >
          {capacity !== undefined && (
            <span
              className={`${prodStyles.chip} ${mode ? prodStyles.chip_dark : ""}`}
            >
              {capacity.toLocaleString()} pcs/day
            </span>
          )}
          {workingHour !== undefined && (
            <span
              className={`${prodStyles.chip} ${mode ? prodStyles.chip_dark : ""}`}
            >
              {workingHour} hrs/day
            </span>
          )}
        </div>
      )}

      <div
        style={{
          position: "absolute",
          right: 10,
          top: 10,
          height: 35,
          width: 35,
          borderRadius: "50%",
          background: mode ? "#232529" : "#fdeaea",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div>
          <DeleteIcon />
        </div>
      </div>
    </div>
  );
};

export default ProdLineInfoCard;
